import { inject, Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { catchError, EMPTY, Observable, Subscription } from 'rxjs';
import { SailsSocketClient } from 'src/sails-io-globals';
import { XAlertService } from './x-alert.service';

@Injectable({
  providedIn: 'root',
})
export class SailsIoService {
  private xAlertService = inject(XAlertService);
  private readonly socketClient: SailsSocketClient | null = null;
  private readonly listenerCountsByEvent = new Map<string, number>();

  private static readonly ABSOLUTE_URL_PATTERN =
    /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//;

  constructor() {
    if (!window.io || !window.io.sails) {
      console.warn(
        'Sails.io.js is not loaded. Please ensure it is included in the project.',
      );

      return;
    }

    const { baseUrl, path } = this.parsePathOrUrl(
      environment.apiUrl + '/socket.io',
    );

    window.io.sails.url = baseUrl;
    window.io.sails.path = path;
    window.io.sails.transports = ['websocket'];

    this.socketClient = baseUrl
      ? window.io.sails.connect(baseUrl)
      : window.io.sails.connect();

    this.socketClient.on('connect', () => {
      if (this.hasActiveListeners()) {
        this.xAlertService.success('Соединение с сервером восстановлено');
      }
    });

    this.socketClient.on('disconnect', () => {
      if (this.hasActiveListeners()) {
        this.xAlertService.error('Соединение с сервером потеряно');
      }
    });
  }

  listen$<T = unknown>(
    eventName: string,
    subscribe?: () => Observable<unknown>,
    unsubscribe?: () => Observable<unknown>,
  ): Observable<T> {
    return new Observable<T>(observer => {
      const client = this.socketClient;
      const isFirstListener = !this.hasActiveListeners(eventName);
      let isRegistered = false;
      let subscribeSub: Subscription | null = null;

      if (!client) {
        observer.error(new Error('Sails socket client is not initialized.'));
        observer.complete();

        return;
      }

      const handler = (...args: unknown[]) => {
        if (args.length <= 1) {
          observer.next(args[0] as T);

          return;
        }

        observer.next(args as T);
      };

      this.incrementListenerCount(eventName);
      isRegistered = true;
      client.on(eventName, handler);

      if (isFirstListener && subscribe) {
        try {
          subscribeSub = subscribe()
            .pipe(
              catchError(error => {
                if (isRegistered) {
                  isRegistered = false;
                  this.decrementListenerCount(eventName);
                  client.off(eventName, handler);
                }

                observer.error(error);

                return EMPTY;
              }),
            )
            .subscribe();
        } catch (error) {
          if (isRegistered) {
            isRegistered = false;
            this.decrementListenerCount(eventName);
            client.off(eventName, handler);
          }

          observer.error(
            error instanceof Error
              ? error
              : new Error('Failed to start Sails subscription.'),
          );

          return;
        }
      }

      return () => {
        subscribeSub?.unsubscribe();

        if (!isRegistered) {
          return;
        }

        isRegistered = false;
        this.decrementListenerCount(eventName);
        client.off(eventName, handler);

        if (!this.hasActiveListeners(eventName) && unsubscribe) {
          unsubscribe()
            .pipe(
              catchError(error => {
                console.error(
                  `Failed to stop Sails subscription for "${eventName}".`,
                  error,
                );

                return EMPTY;
              }),
            )
            .subscribe();
        }
      };
    });
  }

  private hasActiveListeners(eventName?: string): boolean {
    if (eventName) {
      return (this.listenerCountsByEvent.get(eventName) ?? 0) > 0;
    }

    return this.getActiveListenerCount() > 0;
  }

  private getActiveListenerCount(eventName?: string): number {
    if (eventName) {
      return this.listenerCountsByEvent.get(eventName) ?? 0;
    }

    let total = 0;

    this.listenerCountsByEvent.forEach(count => {
      total += count;
    });

    return total;
  }

  request$<T = unknown>(
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    data?: Record<string, unknown>,
  ): Observable<T> {
    return new Observable<T>(observer => {
      const client = this.socketClient;

      if (!client) {
        observer.error(new Error('Sails socket client is not initialized.'));

        return;
      }

      client[method]<T>(url, data, (body, jwres) => {
        if (jwres.statusCode >= 400) {
          observer.error(jwres.pipe());

          return;
        }

        observer.next(body);
        observer.complete();
      });
    });
  }

  get$<T = unknown>(
    url: string,
    data?: Record<string, unknown>,
  ): Observable<T> {
    return this.request$('get', url, data);
  }

  post$<T = unknown>(
    url: string,
    data?: Record<string, unknown>,
  ): Observable<T> {
    return this.request$('post', url, data);
  }

  put$<T = unknown>(
    url: string,
    data?: Record<string, unknown>,
  ): Observable<T> {
    return this.request$('put', url, data);
  }

  delete$<T = unknown>(
    url: string,
    data?: Record<string, unknown>,
  ): Observable<T> {
    return this.request$('delete', url, data);
  }

  private incrementListenerCount(eventName: string): void {
    const currentCount = this.listenerCountsByEvent.get(eventName) ?? 0;

    this.listenerCountsByEvent.set(eventName, currentCount + 1);
  }

  private decrementListenerCount(eventName: string): void {
    const currentCount = this.listenerCountsByEvent.get(eventName) ?? 0;

    if (currentCount <= 1) {
      this.listenerCountsByEvent.delete(eventName);

      return;
    }

    this.listenerCountsByEvent.set(eventName, currentCount - 1);
  }

  private parsePathOrUrl(pathOrUrl: string): {
    baseUrl: string | undefined;
    path: string;
  } {
    const value = pathOrUrl.trim();

    if (!value) {
      return {
        baseUrl: undefined,
        path: '/',
      };
    }

    if (
      SailsIoService.ABSOLUTE_URL_PATTERN.test(value) ||
      value.startsWith('//')
    ) {
      const parsedUrl = value.startsWith('//')
        ? new URL(`https:${value}`)
        : new URL(value);

      const baseUrl = value.startsWith('//')
        ? `//${parsedUrl.host}`
        : `${parsedUrl.protocol}//${parsedUrl.host}`;

      return {
        baseUrl,
        path: `${parsedUrl.pathname || '/'}${parsedUrl.search}${parsedUrl.hash}`,
      };
    }

    return {
      baseUrl: undefined,
      path: value.startsWith('/') ? value : `/${value}`,
    };
  }
}
